import { EmptyValue } from "@bcc/components/ui";
import { Badge } from "@bcc/components/ui/badge";
import { Card, CardContent } from "@bcc/components/ui/card";
import { Label } from "@bcc/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@bcc/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@bcc/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bcc/components/ui/tabs";
import { getStatusBadgeVariant } from "@bcc/lib/types/invoice";
import { formatCurrency, formatDate } from "@client-shared/utils";
import type { PurchaseOrderInvoice, PurchaseOrderInvoiceLineDetails } from "@shared/types/payout";
import { useTranslation } from "react-i18next";
import { ExpandablePOLineItem } from "./expandable-po-line-item";
import { setInvoiceDetailDrawerTabTestId, setInvoiceDetailDrawerTestId } from "./invoice-detail-drawer.utils";
import { getSafeCurrencyCode } from "./utils";

interface AuditLogEntry {
  id: string;
  action: string;
  actor: string;
  role: string;
  timestamp: string;
}

interface InvoiceDetail {
  auditLog: AuditLogEntry[];
}

interface InvoiceDetailDrawerProps {
  invoice: PurchaseOrderInvoice | null;
  invoiceDetail?: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFetchPODetails: (purchaseOrderId: string) => Promise<PurchaseOrderInvoiceLineDetails[]>;
  showShipToCountry: boolean;
  showKaffeesteuer: boolean;
  showAuditTab?: boolean;
  isLoading?: boolean;
  testId?: string;
}

export function InvoiceDetailDrawer({
  invoice,
  invoiceDetail,
  open,
  onOpenChange,
  onFetchPODetails,
  showShipToCountry,
  showKaffeesteuer,
  showAuditTab = true,
  isLoading = false,
  testId,
}: InvoiceDetailDrawerProps) {
  const { t } = useTranslation();
  if (!invoice) return null;

  const safeCurrencyCode = getSafeCurrencyCode(invoice.currencyCode);
  const lineItemsColSpan = 11 - (showShipToCountry ? 0 : 1) - (showKaffeesteuer ? 0 : 1);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        testId={testId ?? setInvoiceDetailDrawerTestId("content")}
        side="right"
        className="w-full sm:max-w-[1100px] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle>{t("components.invoiceDetailDrawer.title")}</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4 py-6">
            <div className="h-8 animate-pulse rounded bg-muted" />
            <div className="h-64 animate-pulse rounded bg-muted" />
          </div>
        ) : (
          <Tabs defaultValue="summary" className="mt-6">
            <TabsList className={`grid w-full ${showAuditTab ? "grid-cols-3" : "grid-cols-2"}`}>
              <TabsTrigger value="summary" data-testid={setInvoiceDetailDrawerTabTestId("summary")}>
                {t("components.invoiceDetailDrawer.tabs.summary")}
              </TabsTrigger>
              <TabsTrigger value="line-items" data-testid={setInvoiceDetailDrawerTabTestId("line-items")}>
                {t("components.invoiceDetailDrawer.tabs.lineItems")}
              </TabsTrigger>
              {showAuditTab && (
                <TabsTrigger value="audit" data-testid={setInvoiceDetailDrawerTabTestId("audit")}>
                  {t("components.invoiceDetailDrawer.tabs.auditLog")}
                </TabsTrigger>
              )}
            </TabsList>

            {/* Summary Tab */}
            <TabsContent value="summary" className="space-y-4">
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.invoiceId")}</Label>
                      <p className="font-semibold">{invoice.invoiceId}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.amount")}</Label>
                      <p className="text-2xl font-bold">{formatCurrency(invoice.invoiceAmount, safeCurrencyCode)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.status")}</Label>
                      <div>
                        <Badge variant={getStatusBadgeVariant(invoice.invoiceStatus)}>{invoice.invoiceStatus}</Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.invoiceDate")}</Label>
                      <p>{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : <EmptyValue />}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.dueDate")}</Label>
                      <p>{invoice.dueDate ? formatDate(invoice.dueDate) : <EmptyValue />}</p>
                    </div>
                    {invoice.lastSettleDate && (
                      <div>
                        <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.payDate")}</Label>
                        <p>{formatDate(invoice.lastSettleDate)}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.totalOrders")}</Label>
                      <p className="font-semibold">{invoice.poCount}</p>
                    </div>
                    {invoice.invoiceLines.length > 0 && invoice.invoiceLines[0]?.vatRate != null && (
                      <div>
                        <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.vatRate")}</Label>
                        <p className="font-semibold">{invoice.invoiceLines[0].vatRate}%</p>
                      </div>
                    )}
                    {invoice.invoiceLines.some((l) => l.vatAmount != null) && (
                      <div>
                        <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.totalVatAmount")}</Label>
                        <p className="font-semibold">
                          {formatCurrency(
                            invoice.invoiceLines.reduce((sum, l) => sum + (l.vatAmount ?? 0), 0),
                            safeCurrencyCode
                          )}
                        </p>
                      </div>
                    )}
                    {showKaffeesteuer && invoice.invoiceLines.some((l) => l.ksAmount != null) && (
                      <div>
                        <Label className="text-muted-foreground">{t("components.invoiceDetailDrawer.labels.totalKaffeesteuer")}</Label>
                        <p className="font-semibold">
                          {formatCurrency(
                            invoice.invoiceLines.reduce((sum, l) => sum + (l.ksAmount ?? 0), 0),
                            safeCurrencyCode
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Line Items Tab */}
            <TabsContent value="line-items" className="space-y-4">
              <div className="rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("components.invoiceDetailDrawer.table.poNumber")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.table.orderDate")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.table.shipDate")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.table.orderAmountBeforeTax")}</TableHead>
                      {showShipToCountry && <TableHead>{t("components.invoiceDetailDrawer.table.shipToCountry")}</TableHead>}
                      {showKaffeesteuer && <TableHead>{t("components.invoiceDetailDrawer.table.kaffeesteuer")}</TableHead>}
                      <TableHead>{t("components.invoiceDetailDrawer.table.vatRate")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.table.vatAmount")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.labels.amount")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.table.shippingFee")}</TableHead>
                      <TableHead>{t("components.invoiceDetailDrawer.table.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.invoiceLines && invoice.invoiceLines.length > 0 ? (
                      invoice.invoiceLines.map((line) => (
                        <ExpandablePOLineItem
                          key={line.purchaseOrderId}
                          invoice={invoice}
                          line={line}
                          currencyCode={safeCurrencyCode}
                          onFetchDetails={onFetchPODetails}
                          showShipToCountry={showShipToCountry}
                          showKaffeesteuer={showKaffeesteuer}
                          testId={setInvoiceDetailDrawerTestId(`line-item-${line.purchaseOrderId}`)}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={lineItemsColSpan} className="text-center text-muted-foreground">
                          {t("components.invoiceDetailDrawer.noLineItems")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Audit Log Tab */}
            {showAuditTab && (
              <TabsContent value="audit" className="space-y-4">
                <div className="space-y-3">
                  {invoiceDetail?.auditLog?.map((entry: AuditLogEntry) => (
                    <Card key={entry.id}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <p className="font-medium">{entry.action}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{entry.actor}</span>
                              <span>•</span>
                              <Badge variant="outline" className="text-xs">
                                {entry.role}
                              </Badge>
                            </div>
                          </div>
                          <time className="text-sm text-muted-foreground">
                            {new Date(entry.timestamp).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </time>
                        </div>
                      </CardContent>
                    </Card>
                  )) || <div className="text-center text-muted-foreground py-8">{t("components.invoiceDetailDrawer.noAuditLog")}</div>}
                </div>
              </TabsContent>
            )}
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
